import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { FormField, FormSection } from "@/components/forms/FormField";
import { PersonFields } from "@/components/forms/PersonFields";
import { PasswordFields } from "@/components/forms/PasswordFields";
import { AddressFields } from "@/components/forms/AddressFields";
import { PlanPicker } from "@/components/forms/PlanPicker";
import { EMPTY_ADDRESS, EMPTY_PERSON, EMAIL_RE, addressErrors, passwordErrors, personErrors, validPiva } from "@/lib/validators";

const companyErrors = (c) => {
  const e = {};
  if (c.company_name.trim().length < 2) e.company_name = "Inserisci il nome dell'azienda";
  if (!validPiva(c.piva)) e.piva = "Partita IVA non valida (11 cifre)";
  if (!c.sdi.trim() && !c.pec.trim()) e.billing = "Inserisci il codice SDI o la PEC";
  if (c.sdi.trim() && !/^[A-Z0-9]{7}$/i.test(c.sdi.trim())) e.sdi = "Codice SDI di 7 caratteri";
  if (c.pec.trim() && !EMAIL_RE.test(c.pec.trim())) e.pec = "PEC non valida";
  if (!c.plan_id) e.plan = "Scegli un piano";
  return e;
};

export const RegisterCompanyForm = ({ onDone }) => {
  const [c, setC] = useState({ company_name: "", piva: "", sdi: "", pec: "", plan_id: "" });
  const [sede, setSede] = useState(EMPTY_ADDRESS);
  const [ref, setRef] = useState(EMPTY_PERSON);
  const [pwd, setPwd] = useState({ password: "", password_confirm: "" });
  const [plans, setPlans] = useState([]);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = { ...companyErrors(c), ...addressErrors(sede), ...personErrors(ref), ...passwordErrors(pwd.password, pwd.password_confirm) };
    setErrors(errs);
    setError("");
    if (Object.keys(errs).length) return setError("Controlla i campi evidenziati");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-company", { ...c, sede, referente: ref, ...pwd });
      onDone(data.email);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-10" data-testid="register-company-form">
      <FormSection title="Azienda">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="company-name" label="Nome azienda" error={errors.company_name}>
            <Input id="company-name" className="h-12" value={c.company_name} onChange={set("company_name")} data-testid="company-name-input" />
          </FormField>
          <FormField id="company-piva" label="Partita IVA" error={errors.piva}>
            <Input id="company-piva" inputMode="numeric" maxLength={13} className="h-12" value={c.piva} onChange={set("piva")} data-testid="company-piva-input" />
          </FormField>
        </div>
      </FormSection>
      <FormSection title="Sede"><AddressFields value={sede} onChange={setSede} errors={errors} prefix="company-sede" /></FormSection>
      <FormSection title="Referente"><PersonFields value={ref} onChange={setRef} errors={errors} prefix="company-ref" /></FormSection>
      <FormSection title="Password di accesso">
        <PasswordFields prefix="company" value={pwd.password} confirm={pwd.password_confirm} errors={errors}
          onChange={(e) => setPwd({ ...pwd, password: e.target.value })} onConfirm={(e) => setPwd({ ...pwd, password_confirm: e.target.value })} />
      </FormSection>
      <FormSection title="Piano">
        <PlanPicker plans={plans} value={c.plan_id} onChange={(id) => setC({ ...c, plan_id: id })} error={errors.plan} />
      </FormSection>
      <FormSection title="Fatturazione">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="company-sdi" label="Codice SDI" error={errors.sdi}>
            <Input id="company-sdi" maxLength={7} className="h-12 uppercase" value={c.sdi} onChange={set("sdi")} data-testid="company-sdi-input" />
          </FormField>
          <FormField id="company-pec" label="oppure PEC" error={errors.pec}>
            <Input id="company-pec" type="email" className="h-12" value={c.pec} onChange={set("pec")} data-testid="company-pec-input" />
          </FormField>
        </div>
        {errors.billing && <p className="text-xs text-red-600" data-testid="company-billing-error">{errors.billing}</p>}
        <p className="rounded-xl bg-[#F4C20D]/15 px-4 py-3 text-sm text-[#5C4700]" data-testid="payment-notice">
          Il pagamento con addebito automatico (PayPal) sarà attivato a breve: ti avviseremo via email. Per ora nessun addebito.
        </p>
      </FormSection>
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-company-error">{error}</p>}
      <Button type="submit" disabled={loading} className="h-12 w-full rounded-full bg-[#0F2D5E] text-base hover:bg-[#0B2250]" data-testid="register-company-submit-btn">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registra l'azienda
      </Button>
    </form>
  );
};
